// ===========================================
// School Waitlist Landing Page (Public)
// ===========================================
// Invite other music schools to join the platform
// Features piano header, features list, and contact form

import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  Group as UsersIcon,
  SchoolOutlined as BookIcon,
  EventNote as CalendarIcon,
  LibraryMusic as MusicIcon,
  InsertChart as AssessmentIcon,
  CloudUpload as CloudIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import PublicFooter from '../../components/public/PublicFooter';

// ===========================================
// VALIDATION SCHEMA
// ===========================================

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^(\+61|0)[2-478]\d{8}$|^\+?[\d\s-]{10,15}$/;

const waitlistSchema = z.object({
  schoolName: z.string().min(1, 'School name is required').max(100),
  contactName: z.string().min(1, 'Contact name is required').max(100),
  email: z.string().regex(emailRegex, 'Invalid email address'),
  phone: z.string().regex(phoneRegex, 'Invalid phone number'),
  location: z.string().min(1, 'Location is required').max(100),
  studentCount: z.number().min(1, 'Minimum 1 student').max(10000),
  message: z.string().max(500).optional().or(z.literal('')),
});

type WaitlistFormData = z.infer<typeof waitlistSchema>;

// Feature Card Component
const FeatureCard = ({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      p: 2,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      border: '2px solid rgba(69, 128, 228, 0.1)',
      transition: 'all 0.3s ease',
      '&:hover': {
        border: '2px solid primary.main',
        boxShadow: '0 4px 12px rgba(69, 128, 228, 0.15)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
      <Icon sx={{ fontSize: 32, color: 'primary.main' }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Card>
);

export default function WaitlistPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);

  // Form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
    mode: 'onChange',
    defaultValues: {
      schoolName: '',
      contactName: '',
      email: '',
      phone: '',
      location: '',
      studentCount: 50,
      message: '',
    },
  });

  const formValues = watch();

  // Form mutation
  const submitMutation = useMutation({
    mutationFn: async (data: WaitlistFormData) => {
      const res = await apiClient.post<{ status: string; message: string; data: unknown }>(
        '/public/waitlist',
        {
          schoolName: data.schoolName,
          contactName: data.contactName,
          email: data.email,
          phone: data.phone.replace(/[\s-]/g, ''),
          location: data.location,
          studentCount: data.studentCount,
          message: data.message || undefined,
        }
      );
      return res.data;
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  const onSubmit = async (data: WaitlistFormData) => {
    await submitMutation.mutateAsync(data);
  };

  // Success state
  if (submitted) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="sm" sx={{ flex: 1, py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" color="primary" gutterBottom>
              Thank You!
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              You're on the waitlist!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              We've sent a confirmation email to <strong>{formValues.email}</strong>.
              We'll be in touch soon about bringing {formValues.schoolName} to Music 'n Me.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')}>
              Return Home
            </Button>
          </Paper>
        </Container>
        <PublicFooter />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character Decorations - Left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character Decorations - Right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music Notes Decorations */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile Header - Logo centered */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: 120,
            height: 'auto',
            mb: 2,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          Join the Waitlist
        </Typography>
      </Box>

      {/* Desktop Header - Piano with Logo Overlay */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        {/* Piano Background */}
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Logo - top left */}
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        {/* Title overlay */}
        <Typography
          variant="h3"
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'white',
            textAlign: 'center',
            fontSize: { sm: '2rem', md: '2.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          Join the Waitlist
        </Typography>
      </Box>

      {/* Subtitle */}
      <Container maxWidth="md" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          Be among the first music schools to transform how you manage lessons and students
        </Typography>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        {/* Features Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              mb: 4,
              color: 'primary.main',
            }}
          >
            What's Included in Music 'n Me
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={UsersIcon as React.ElementType}
                title="Multi-Location Support"
                description="Manage multiple school locations from a single dashboard with ease."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={BookIcon as React.ElementType}
                title="Hybrid Lesson Model"
                description="Our core differentiator - seamlessly manage group and individual lessons."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={CalendarIcon as React.ElementType}
                title="Smart Scheduling"
                description="Drag-and-drop calendar with automatic conflict detection."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={TrendingIcon as React.ElementType}
                title="Gamification"
                description="Motivate students with achievements, XP, and progress tracking."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={AssessmentIcon as React.ElementType}
                title="Advanced Analytics"
                description="Comprehensive dashboards and reporting for all user roles."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={CloudIcon as React.ElementType}
                title="Google Drive Integration"
                description="Two-way sync with Google Drive for seamless file management."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={MusicIcon as React.ElementType}
                title="Parent Portal"
                description="Parents can view schedules, pay invoices, and book lessons online."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard
                icon={CheckIcon as React.ElementType}
                title="Teacher Tools"
                description="Attendance marking, notes, resources, and progress tracking."
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Form Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Your School Details
              </Typography>

              <Box component="form" sx={{ mt: 2 }}>
                <Controller
                  name="schoolName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="School Name"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      error={!!errors.schoolName}
                      helperText={errors.schoolName?.message}
                    />
                  )}
                />

                <Controller
                  name="contactName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Your Name"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      error={!!errors.contactName}
                      helperText={errors.contactName?.message}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Address"
                      type="email"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      error={!!errors.email}
                      helperText={errors.email?.message || "We'll send updates here"}
                    />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Phone Number"
                      fullWidth
                      required
                      placeholder="04XX XXX XXX"
                      sx={{ mb: 2 }}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />

                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="School Location (City/Region)"
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                      error={!!errors.location}
                      helperText={errors.location?.message}
                    />
                  )}
                />

                <Controller
                  name="studentCount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Approximate Number of Students"
                      type="number"
                      fullWidth
                      required
                      inputProps={{ min: 1, max: 10000 }}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      sx={{ mb: 2 }}
                      error={!!errors.studentCount}
                      helperText={errors.studentCount?.message}
                    />
                  )}
                />

                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Message (Optional)"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Tell us about your school or any specific needs..."
                      error={!!errors.message}
                      helperText={errors.message?.message}
                    />
                  )}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Info & Benefits */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                border: 2,
                borderColor: '#96DAC9',
                bgcolor: 'rgba(150, 218, 201, 0.15)',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Why Join Music 'n Me?
              </Typography>
              <List dense>
                <ListItem disableGutters sx={{ mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Streamlined Operations"
                    secondary="Reduce admin time by 60% with automated scheduling and invoicing"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Increased Enrolment"
                    secondary="Modern parent portal increases trial bookings by 40%"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Better Parent Engagement"
                    secondary="Parents stay updated with notifications and online payments"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Student Motivation"
                    secondary="Gamification keeps students engaged and practicing"
                  />
                </ListItem>
                <ListItem disableGutters sx={{ mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dedicated Support"
                    secondary="Get onboarded by our team with training and setup"
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Flexible Pricing"
                    secondary="Subscription plans tailored to your school's size"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'rgba(255, 206, 0, 0.1)',
                  borderColor: '#FFCE00',
                  border: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  🎵 Early Adopter Benefits
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Be part of our beta launch and get special early pricing, dedicated onboarding, and direct input on feature development.
                </Typography>
              </Paper>
            </Paper>
          </Grid>
        </Grid>

        {/* Submit Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            ← Go Back
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={!isValid || submitMutation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {submitMutation.isPending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Submitting...
              </>
            ) : (
              'Join the Waitlist →'
            )}
          </Button>
        </Box>

        {submitMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to submit. Please try again or contact us directly.
          </Alert>
        )}
      </Container>

      <PublicFooter schoolName="Music 'n Me" schoolSlug="music-n-me" />
    </Box>
  );
}
